import React, { useId, type FocusEvent } from "react";
import clsx from "clsx";
import { Check } from "lucide-react";

import styles from "./checkbox-group.module.css";

export interface CheckboxOption {
  value: string;
  label: string;
}

export interface CheckboxGroupProps {
  id?: string;
  name: string;
  label: string;
  required?: boolean;
  options: CheckboxOption[];
  value: string[]; // Array of selected values
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
  orientation?: "horizontal" | "vertical";
}

export function CheckboxGroup({
  id: idProp,
  name,
  label,
  required = false,
  options,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  orientation = "horizontal",
}: CheckboxGroupProps) {
  const reactId = useId().replace(/:/g, "");
  const baseId = idProp ?? `checkbox-group-${reactId}`;
  const errorId = `${baseId}-error`;
  const hasError = error != null && error !== "";
  const vertical = orientation === "vertical";

  const handleBlur = (e: FocusEvent<HTMLFieldSetElement>) => {
    const next = e.relatedTarget as Node | null;
    if (next != null && e.currentTarget.contains(next)) return;
    onBlur?.();
  };

  const handleToggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  return (
    <fieldset
      className={styles.wrapper}
      disabled={disabled}
      onBlur={handleBlur}
      aria-invalid={hasError ? true : undefined}
      aria-describedby={hasError ? errorId : undefined}
    >
      <legend className={clsx("sub-heading-md", styles.label)}>
        {label}
        {required ? (
          <span className="text-danger text-nowrap" aria-hidden>
            {"\u00A0*"}
          </span>
        ) : null}
      </legend>

      <div
        className={clsx(styles.options, vertical && styles.optionsVertical)}
        role="presentation"
      >
        {options.map((opt) => {
          const inputId = `${baseId}-${opt.value}`;
          const isChecked = value.includes(opt.value);

          return (
            <label
              key={opt.value}
              className={clsx(styles.option, disabled && styles.optionDisabled)}
              htmlFor={inputId}
            >
              <span className={styles.control}>
                <input
                  id={inputId}
                  type="checkbox"
                  name={name}
                  className={styles.nativeInput}
                  checked={isChecked}
                  disabled={disabled}
                  onChange={() => handleToggle(opt.value)}
                />
                <span
                  className={clsx(styles.box, isChecked && styles.boxChecked)}
                  aria-hidden
                >
                  {isChecked && (
                    <Check 
                      className={styles.icon} 
                      size={12} 
                      strokeWidth={3} 
                    />
                  )}
                </span>
              </span>
              <span className="body-sm">
                {opt.label}
              </span>
            </label>
          );
        })}
      </div>

      {hasError && (
        <p id={errorId} className={clsx(styles.message, styles.messageError)} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}